import * as signalR from "@microsoft/signalr";
import { JWT, SHOW_SNACK_INFO, SET_CHAT_UNREAD } from "@/store/MUTATIONS-TYPES";
import store from "@/store/store";
import { ChatService } from "@/services/chatService";
import { playReceivedSound, unlockChatSounds } from "@/services/chatSounds";

let connection: signalR.HubConnection | null = null;
let startPromise: Promise<void> | null = null;
let activeConversationId: number | null = null;
const joinOwners = new Map<number, Set<string>>();
const messageListeners: Array<(message: any) => void> = [];
const conversationListeners: Array<(payload: any) => void> = [];
const closedListeners: Array<(payload: any) => void> = [];
/** Dedupe SignalR MessageReceived echoes (same socket in multiple groups). */
const recentMessageKeys = new Map<string, number>();
const MESSAGE_DEDUPE_MS = 5000;

function messageEventKey(message: any): string {
  const id = message?.id ?? message?.Id;
  const clientId = message?.clientMessageId ?? message?.ClientMessageId;
  const conversationId = message?.conversationId ?? message?.ConversationId;
  if (id != null && id !== "" && Number(id) > 0) return `id:${id}`;
  if (clientId) return `client:${clientId}`;
  return `fallback:${conversationId}:${message?.body ?? message?.Body ?? ""}:${message?.creationDate ?? message?.CreationDate ?? ""}`;
}

function isDuplicateMessageEvent(message: any): boolean {
  const key = messageEventKey(message);
  const now = Date.now();
  for (const [k, at] of recentMessageKeys) {
    if (now - at > MESSAGE_DEDUPE_MS) recentMessageKeys.delete(k);
  }
  if (recentMessageKeys.has(key)) return true;
  recentMessageKeys.set(key, now);
  return false;
}

function hubUrl() {
  const base = (process.env.VUE_APP_API || "").replace(/\/?$/, "/");
  return `${base}hubs/chat`;
}

function isViewingConversation(conversationId: number | string) {
  const id = Number(conversationId);
  if (!Number.isFinite(id) || id <= 0) return false;
  const viewing = store.state.app.chatViewingConversationId;
  if (viewing != null && Number(viewing) === id) return true;
  if (store.state.app.chatPanelOpen && Number(store.state.app.chatPanelConversationId) === id) return true;
  return false;
}

export function isConversationJoined(conversationId: number) {
  const owners = joinOwners.get(conversationId);
  return Boolean(owners && owners.size > 0);
}

function myPersonId() {
  return Number(store.getters.currentSessionUser?.personId || 0);
}

export function parseUnreadCount(payload: any): number {
  const result = payload?.result ?? payload?.Result ?? payload;
  if (typeof result === "number") return Number(result) || 0;
  const raw =
    result?.unreadCount ??
    result?.UnreadCount ??
    payload?.unreadCount ??
    payload?.UnreadCount ??
    0;
  return Number(raw) || 0;
}

export async function refreshChatUnread() {
  if (!localStorage.getItem(JWT)) {
    store.commit(SET_CHAT_UNREAD, 0);
    return;
  }
  try {
    const response = await ChatService.getUnreadCountAsync();
    store.commit(SET_CHAT_UNREAD, parseUnreadCount(response));
  } catch (_) {}
}

export async function bootstrapChatSession() {
  if (!localStorage.getItem(JWT)) {
    await stopChatHub();
    store.commit(SET_CHAT_UNREAD, 0);
    return;
  }
  unlockChatSounds();
  await refreshChatUnread();
  await startChatHub();
}

export async function startChatHub() {
  const token = localStorage.getItem(JWT);
  if (!token) {
    await stopChatHub();
    return;
  }

  if (connection?.state === signalR.HubConnectionState.Connected) return;
  if (startPromise) {
    await startPromise;
    return;
  }

  startPromise = (async () => {
    if (connection) {
      try {
        await connection.stop();
      } catch (_) {}
      connection = null;
    }

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl(), {
        accessTokenFactory: () => localStorage.getItem(JWT) || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .build();

    hub.on("UnreadCountChanged", (payload: any) => {
      store.commit(SET_CHAT_UNREAD, parseUnreadCount(payload));
    });

    hub.on("MessageReceived", (message: any) => {
      if (isDuplicateMessageEvent(message)) return;

      const conversationId = message?.conversationId ?? message?.ConversationId;
      const body = message?.body ?? message?.Body ?? "";
      const sender = message?.senderName ?? message?.SenderName ?? "Nova mensagem";
      const senderPersonId = Number(message?.senderPersonId ?? message?.SenderPersonId ?? 0);
      const mine = senderPersonId > 0 && senderPersonId === myPersonId();

      messageListeners.forEach((listener) => listener(message));

      if (!mine && !isViewingConversation(conversationId)) {
        store.commit(SHOW_SNACK_INFO, `${sender}: ${body}`);
        refreshChatUnread();
        playReceivedSound();
      } else if (!mine && isViewingConversation(conversationId)) {
        playReceivedSound();
      }
    });

    hub.on("ConversationUpdated", (payload: any) => {
      conversationListeners.forEach((listener) => listener(payload));
    });

    hub.on("ConversationClosed", (payload: any) => {
      closedListeners.forEach((listener) => listener(payload));
    });

    hub.onreconnected(async () => {
      await refreshChatUnread();
      if (activeConversationId) {
        try {
          await hub.invoke("JoinConversation", activeConversationId);
        } catch (e) {
          console.error(e);
        }
      }
    });

    try {
      await hub.start();
      connection = hub;
      if (activeConversationId) {
        try {
          await hub.invoke("JoinConversation", activeConversationId);
        } catch (e) {
          console.error(e);
        }
      }
    } catch (e) {
      console.error("Falha ao conectar o chat:", e);
      connection = null;
      try {
        await hub.stop();
      } catch (_) {}
    }
  })();

  try {
    await startPromise;
  } finally {
    startPromise = null;
  }
}

export async function stopChatHub() {
  activeConversationId = null;
  joinOwners.clear();
  if (!connection) return;
  try {
    await connection.stop();
  } catch (e) {
    console.error(e);
  }
  connection = null;
}

export async function joinChatConversation(conversationId: number, owner = "default") {
  if (!Number.isFinite(conversationId) || conversationId <= 0) return;
  let owners = joinOwners.get(conversationId);
  if (!owners) {
    owners = new Set();
    joinOwners.set(conversationId, owners);
  }
  owners.add(owner);
  activeConversationId = conversationId;

  await startChatHub();
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try {
    await connection.invoke("JoinConversation", conversationId);
  } catch (e) {
    console.error(e);
  }
}

export async function leaveChatConversation(conversationId: number, owner = "default") {
  if (!Number.isFinite(conversationId) || conversationId <= 0) return;
  const owners = joinOwners.get(conversationId);
  if (owners) {
    owners.delete(owner);
    if (owners.size > 0) return;
    joinOwners.delete(conversationId);
  }

  if (activeConversationId === conversationId) {
    activeConversationId = null;
    for (const [id] of joinOwners) {
      activeConversationId = id;
      break;
    }
  }

  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try {
    await connection.invoke("LeaveConversation", conversationId);
  } catch (e) {
    console.error(e);
  }
}

/** REST is source of truth; SignalR only pushes live updates. */
export async function sendChatMessage(conversationId: number, body: string, clientMessageId?: string) {
  if (!Number.isFinite(conversationId) || conversationId <= 0) {
    throw new Error("invalid conversation id");
  }
  unlockChatSounds();
  const response = await ChatService.sendMessageAsync(conversationId, body, clientMessageId);
  if (response && (response as any).success === false) {
    throw new Error(String((response as any).result || "Falha ao enviar"));
  }
  return response;
}

export function getChatHub() {
  return connection;
}

export function getActiveConversationId() {
  return activeConversationId;
}

export function onChatMessage(listener: (message: any) => void) {
  messageListeners.push(listener);
  return () => {
    const index = messageListeners.indexOf(listener);
    if (index >= 0) messageListeners.splice(index, 1);
  };
}

export function onConversationUpdated(listener: (payload: any) => void) {
  conversationListeners.push(listener);
  return () => {
    const index = conversationListeners.indexOf(listener);
    if (index >= 0) conversationListeners.splice(index, 1);
  };
}

export function onConversationClosed(listener: (payload: any) => void) {
  closedListeners.push(listener);
  return () => {
    const index = closedListeners.indexOf(listener);
    if (index >= 0) closedListeners.splice(index, 1);
  };
}
